import React, {useEffect, useState} from "react";
import Page from "../../Components/Page";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import EditableReactTableV2 from "../../Components/ReactTable/EditableReactTableV2";
import {useParams} from "react-router-dom";
import {Breadcrumbs} from "../../Components/Breadcrumbs";
import {getStringParamOrDefault} from "../../../utils/urlBuilder";
import _ from "lodash";
import NameValueLabels from "../../Components/NameValueLabels";
import ReactTable from "../../Components/ReactTable";
import {makeApiCall} from "../../../Api/makeApiCall";
import {
    EditFormDateInputField,
    EditFormDropdownInputField,
    EditFormNumberInputField,
    EditFormTextInputField
} from "../../Components/ReactTable/EditRecordForm";
import {INDEX_FIELD} from "../../Components/ReactTable/common_custom_columns";
import {useServiceBase} from "../../Components/ReactTable/useServiceBase";
import {getCellForService, getFilterValueForService} from "../../Components/ReactTable/common_dropdown_options";
import {getUserEmail} from "../../../auth/accessTokenValidator";
import {setValuesToNullIfEmptyOrUndefined} from "../../Components/ReactTable/helpers";
import SelectDropdown from "../../Components/Dropdowns/SelectDropdown";
import CsvDownload from "../../../utils/CsvDownloadWithNulls";
import MerchantDetailsModal from "./MerchantDetailsModal";

function MerchantDetails() {
    const params = useParams();
    const merchantId = params.merchantId;
    const brandId = getStringParamOrDefault('brandId');
    const brandName = getStringParamOrDefault('brandName');
    const merchantName = getStringParamOrDefault('merchantName');
    const startDate = getStringParamOrDefault('startDate');
    const endDate = getStringParamOrDefault('endDate');


    const [merchantDetails, setMerchantDetails] = useState([]);
    const [mapEnforcementLogs, setMapEnforcementLogs] = useState([]);
    const [distributorOptions, setDistributorOptions] = useState([]);
    const [mapEnforcementLogsActionsOptions, setMapEnforcementLogsActionsOptions] = useState([]);
    const [brandOnboardingMap, setBrandOnboardingMap] = useState([]);
    const [decodeLoading, setDecodeLoading] = useState(true);
    const [refresh, setRefresh] = useState(undefined);

    const endpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/mapEnforcementLogs/`;
    const {loading, data, onRowEditConfirmed, onRowDeleteConfirmed} = useServiceBase({endpointUrl});

    useEffect(() => {
        if (!_.isEmpty(data)) {
            const mapEnforcementLogData = data.data.filter((log) => log.brandId === parseInt(brandId) && log.merchantId === merchantId);
            setMapEnforcementLogs(mapEnforcementLogData);
        }
    }, [data])

    function transformData(data) {
        return data
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map(item => ({
                "ASIN": item.asin,
                "ASIN Title": item.asin_title,
                "UPC": item.upc,
                "Merchant Id": item.merchant_id,
                "Price": (Math.round(item.price * 100) / 100).toFixed(2),
                "MAP": item.MAP,
                "Pack Size": item.pack_size,
                "Price Violation": item.price_violation,
                "FBA Violation": item.fba_violation,
                "Promo": item.promo,
                "Date": item.date
            }));
    }

    function downloadCsv() {
        if (!loading)
            CsvDownload(transformData(merchantDetails?.violations), `${brandName}_${merchantName}_${startDate}_${endDate}_violations.csv`)
    }

    useEffect(() => {
        setDecodeLoading(true);
        Promise.all([
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/clientDashboard?brand_id=${brandId}&start_date=${startDate}&merchant_id=${merchantId}&end_date=${endDate}`),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/map_distributor_options`),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/mapEnforcementLogsActions`),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/brandOnboarding/`)
        ]).then(([
                     clientDashboardResults,
                     distributorOptionResult,
                     mapEnforcementLogsActionsResults,
                     brandOnboardingResults
                 ]) => {
            setMerchantDetails(!_.isEmpty(clientDashboardResults) ? clientDashboardResults[0] : []);
            setDistributorOptions(distributorOptionResult.data.map((distributor) => ({
                "value": distributor.index,
                "label": distributor.name
            })));

            setMapEnforcementLogsActionsOptions(mapEnforcementLogsActionsResults.data.map((distributor) => ({
                "value": distributor.index,
                "label": distributor.name
            })));
            setBrandOnboardingMap(brandOnboardingResults.map(brand => ({value: brand.brand_id, label: brand.brand_name})))
            setDecodeLoading(false);
        }).finally(() => setDecodeLoading(false));

    }, [refresh]);

    const calculatePrice = price => (Math.round(price * 100) / 100).toFixed(2);

    const customColumnsViolations = [
        {header: "ASIN", accessorKey: "asin",
        cell: ({row}) => <a
            href={"https://www.amazon.com/dp/"+ row.original.asin +"/ref=sr_1_1?m=" + merchantId + "&marketplaceID=ATVPDKIKX0DER"}
            rel="noopener noreferrer"
            target="_blank">{row.original.asin}</a>},
        {header: "UPC", accessorKey: "upc"},
        {header: "Title", accessorKey: "asin_title"},
        {header: "Price", accessorKey: "price", accessorFn: (row) => { return row.price ? calculatePrice(row.price) : "0.00"; }},
        {header: "MAP", accessorKey: "MAP"},
        {header: "Pack", accessorKey: "pack_size"},
        {header: "Pack-Adjusted MAP", id: "pack_adjusted_map", accessorFn: (row) => {
                return row.pack_size && row.price && row.pack_size > 1 ?
                    (row.pack_size * calculatePrice(row.price)).toFixed(2) :
                    undefined
            }
        },
        {header: "Price Violations", accessorKey: "price_violation"},
        {header: "FBA Violations", accessorKey: "fba_violation"},
        {header: "Promo", accessorKey: "promo"},
        {header: "Date", accessorKey: "date"}
    ];

    const customColumnsEnforcementLog = [
        {
            header: "Distributor",
            accessorKey: "distributor",
            cell: ({getValue}) => getCellForService(getValue(), distributorOptions),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, distributorOptions),
        },
        {
            header: "Action",
            accessorKey: "action",
            cell: ({getValue}) => getCellForService(getValue(), mapEnforcementLogsActionsOptions),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, mapEnforcementLogsActionsOptions),
        },
        {
            header: "Action By",
            accessorKey: "actionBy",
        },
        {
            header: "Contact Date",
            accessorKey: "contactDate",
        }
    ];

    const onRowEditConfirmedWrapper = newOrUpdatedRow => {
        newOrUpdatedRow.actionBy = getUserEmail();
        const sanitizedData = setValuesToNullIfEmptyOrUndefined(newOrUpdatedRow);
        return onRowEditConfirmed(sanitizedData);
    };

    const validatePreSave = ({newData, rows, form}) => {
        let hasDuplicate = false;
        const indexValue = form.getValues('index');
        const errors = []

        rows.forEach(row => {
            if (row.original.merchantId === newData.merchantId && row.original.action === newData.action && row.original.brandId === newData.brandId
                && row.original.contactDate === newData.contactDate && row.original.distributor === newData.distributor && indexValue !== row.original.index) {
                hasDuplicate = true;
            }
        });

        if (hasDuplicate) {
            errors.push('Duplicate entry detected. Please remove the duplicate entry before saving.')
        }

        if (new Date(newData.contactDate).getYear() >= 10000) {
            errors.push('Invalid year')
        }

        if (errors.length > 0) {
            form.setError('contactDate', {type: 'custom', message: errors});
        }

        return errors.length === 0;
    }

    return (
        <Page name={`Merchant: ${merchantName} - (${merchantId})`}
              breadCrumbs={<Breadcrumbs items={[
                  {label: "MAP Enforcement Dashboard", to: '/mapEnforcementDashboard/'},
                  {label: "Client Management", to: '/clientDashboard/' + brandId + '?brandName=' + brandName},
                  {label: "Merchant Details"}
              ]}/>}
        >
            <LoadingWrapper loading={decodeLoading || loading}>
                <div className={"merchant-details-container"}
                     data-testid={"contact-info-container"}>
                    <h5>Contact Info</h5>
                    <MerchantDetailsModal data={merchantDetails} brandOnboardingDecode={brandOnboardingMap} refreshData={setRefresh}/>
                    <div className={'contact-info-container'}>
                        <NameValueLabels className={"merchant-details"} labelValuePairs={[
                            {label: "Business Name", value: merchantDetails?.business_name},
                            {label: "Address", value: merchantDetails?.address},
                            {label: "Contact Phone", value: merchantDetails?.contact_phone},
                            {label: "Contact Name", value: merchantDetails?.contact_name},
                            {label: "Contact Email", value: merchantDetails?.contact_email},
                            {label: "Merchant Brand", value: getCellForService(merchantDetails?.merchant_brand_id, brandOnboardingMap)},
                        ]}/>
                    </div>
                </div>
                <div className={"merchant-details-notes-container"}
                     data-testid={"notes-container"}>
                    <h5>Notes</h5>
                    <textarea disabled value={merchantDetails?.notes} data-testid={"notes-textarea"}/>
                </div>
                <div data-testid={"violations-container"}>
                    <div className={"merchant-details-violations-container"}>
                        <h5>Violations</h5>
                        <div>
                            <button className='button'
                                    data-testid={'client-dashboard-csv-button'}
                                    onClick={() => downloadCsv()}
                            >
                                Export Csv
                            </button>
                        </div>
                    </div>

                    <ReactTable filterable
                                data={merchantDetails?.violations || []}
                                columns={customColumnsViolations}
                                initialState={{pagination: {pageSize: 10}}}
                                hasStickyHeader={false}
                    />
                </div>
                <div data-testid={"enforcement-log-container"}>
                    <h5>Enforcement Log</h5>
                    <EditableReactTableV2
                        filterable
                        data={mapEnforcementLogs}
                        columns={customColumnsEnforcementLog}
                        validatePreSave={validatePreSave}
                        hasStickyHeader={false}
                        editTable={[
                            INDEX_FIELD,
                            {id: "brandId", defaultValue: brandId, Component: args => <EditFormNumberInputField{...args} label={"Brand"} disabled/>},
                            {id: "merchantId", defaultValue: merchantId, Component: args => <EditFormTextInputField{...args} label={"Merchant Id"} disabled/>},
                            {id: "distributor", Component: args => <EditFormDropdownInputField {...args} options={distributorOptions} isRequired/>},
                            {id: "action", Component: args => <EditFormDropdownInputField {...args} options={mapEnforcementLogsActionsOptions} isRequired/>},
                            {id: "actionBy", Component: args => <EditFormTextInputField{...args} disabled/>},
                            {id: "contactDate", Component: args => <EditFormDateInputField{...args} isRequired/>},
                        ]}
                        onRowEditConfirmed={onRowEditConfirmedWrapper}
                        onRowDeleteConfirmed={onRowDeleteConfirmed}
                    >
                        {
                            ({}) => {
                                return (
                                    <div className={'enforcement-logs-decodes-container'} data-testid={'enforcement-logs-decodes-container'}>
                                        <SelectDropdown
                                            options={[
                                                {label: 'Map Distributor', value: 'Map Distributor', url: '/mapDistributor'},
                                                {label: 'Enforcement Actions', value: 'Enforcement Actions', url: '/mapEnforcementLogsActions'}
                                            ]}
                                            id={'mapEnforcementDecodes_dropdown'}
                                            selectValue={'Enforcement Decodes...'}
                                            onChange={o => window.open(o.url, '_blank')}
                                        />
                                    </div>
                                )
                            }
                        }
                    </EditableReactTableV2>
                </div>
            </LoadingWrapper>
        </Page>
    );
}

export default MerchantDetails;
